import { Profile, SubjectProgress } from "./type.app.js";

export type Achievement = {
  id: string;
  family: string;
  subject: "reading" | "math" | "spelling" | "shared";
  number: number;
  title: string;
  description: string;
  earned: (profile: Profile) => boolean;
};

const retiredCount = (profile: Profile): number => {
  return Object.values(profile.textStats).filter((stat) => stat.retired).length;
};

const rightCount = (profile: Profile): number => {
  return Object.values(profile.textStats).reduce((sum, stat) => sum + stat.correct, 0);
};

const mathProgress = (profile: Profile): SubjectProgress | undefined => profile.subjectProgress?.math;

const mathRightCount = (profile: Profile): number =>
  mathProgress(profile)?.events.filter((event) => event.result === "right").length ?? 0;

const spellingProgress = (profile: Profile): SubjectProgress | undefined => profile.subjectProgress?.spelling;

const spellingRightCount = (profile: Profile): number =>
  spellingProgress(profile)?.events.filter((event) => event.result === "right").length ?? 0;

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: "streak-5",
    family: "streak",
    subject: "reading",
    number: 5,
    title: "5 in a row",
    description: "Get 5 right in a row",
    earned: (profile) => profile.maxCorrectStreak >= 5,
  },
  {
    id: "streak-10",
    family: "streak",
    subject: "reading",
    number: 10,
    title: "10 in a row",
    description: "Get 10 right in a row",
    earned: (profile) => profile.maxCorrectStreak >= 10,
  },
  {
    id: "streak-20",
    family: "streak",
    subject: "reading",
    number: 20,
    title: "20 in a row",
    description: "Get 20 right in a row",
    earned: (profile) => profile.maxCorrectStreak >= 20,
  },
  {
    id: "shop-3",
    family: "shop",
    subject: "shared",
    number: 3,
    title: "Buy 3 items",
    description: "Buy 3 shop rewards",
    earned: (profile) => profile.inventory.length >= 3,
  },
  {
    id: "shop-10",
    family: "shop",
    subject: "shared",
    number: 10,
    title: "Buy 10 items",
    description: "Buy 10 shop rewards",
    earned: (profile) => profile.inventory.length >= 10,
  },
  {
    id: "shop-20",
    family: "shop",
    subject: "shared",
    number: 20,
    title: "Buy 20 items",
    description: "Buy 20 shop rewards",
    earned: (profile) => profile.inventory.length >= 20,
  },
  {
    id: "bank-20",
    family: "bank",
    subject: "shared",
    number: 20,
    title: "20 in the bank",
    description: "Have 20 coins at once",
    earned: (profile) => profile.peakCoins >= 20,
  },
  {
    id: "bank-50",
    family: "bank",
    subject: "shared",
    number: 50,
    title: "50 in the bank",
    description: "Have 50 coins at once",
    earned: (profile) => profile.peakCoins >= 50,
  },
  {
    id: "bank-100",
    family: "bank",
    subject: "shared",
    number: 100,
    title: "100 in the bank",
    description: "Have 100 coins at once",
    earned: (profile) => profile.peakCoins >= 100,
  },
  {
    id: "earned-25",
    family: "earned",
    subject: "shared",
    number: 25,
    title: "Earn 25 coins",
    description: "Earn 25 coins in all",
    earned: (profile) => profile.coinsEarned >= 25,
  },
  {
    id: "earned-100",
    family: "earned",
    subject: "shared",
    number: 100,
    title: "Earn 100 coins",
    description: "Earn 100 coins in all",
    earned: (profile) => profile.coinsEarned >= 100,
  },
  {
    id: "earned-250",
    family: "earned",
    subject: "shared",
    number: 250,
    title: "Earn 250 coins",
    description: "Earn 250 coins in all",
    earned: (profile) => profile.coinsEarned >= 250,
  },
  {
    id: "level-5",
    family: "level",
    subject: "reading",
    number: 5,
    title: "Level 5",
    description: "Reach reading level 5",
    earned: (profile) => profile.level >= 5,
  },
  {
    id: "level-20",
    family: "level",
    subject: "reading",
    number: 20,
    title: "Level 20",
    description: "Reach reading level 20",
    earned: (profile) => profile.level >= 20,
  },
  {
    id: "level-50",
    family: "level",
    subject: "reading",
    number: 50,
    title: "Level 50",
    description: "Reach reading level 50",
    earned: (profile) => profile.level >= 50,
  },
  {
    id: "read-10",
    family: "read",
    subject: "reading",
    number: 10,
    title: "10 right",
    description: "Get 10 answers right",
    earned: (profile) => rightCount(profile) >= 10,
  },
  {
    id: "read-50",
    family: "read",
    subject: "reading",
    number: 50,
    title: "50 right",
    description: "Get 50 answers right",
    earned: (profile) => rightCount(profile) >= 50,
  },
  {
    id: "read-200",
    family: "read",
    subject: "reading",
    number: 200,
    title: "200 right",
    description: "Get 200 answers right",
    earned: (profile) => rightCount(profile) >= 200,
  },
  {
    id: "master-5",
    family: "master",
    subject: "reading",
    number: 5,
    title: "Master 5",
    description: "Master 5 reading texts",
    earned: (profile) => retiredCount(profile) >= 5,
  },
  {
    id: "master-15",
    family: "master",
    subject: "reading",
    number: 15,
    title: "Master 15",
    description: "Master 15 reading texts",
    earned: (profile) => retiredCount(profile) >= 15,
  },
  {
    id: "master-40",
    family: "master",
    subject: "reading",
    number: 40,
    title: "Master 40",
    description: "Master 40 reading texts",
    earned: (profile) => retiredCount(profile) >= 40,
  },
  {
    id: "math-streak-5",
    family: "math-streak",
    subject: "math",
    number: 5,
    title: "5 in a row",
    description: "Get 5 math answers right in a row",
    earned: (profile) => (mathProgress(profile)?.maxCorrectStreak ?? 0) >= 5,
  },
  {
    id: "math-streak-10",
    family: "math-streak",
    subject: "math",
    number: 10,
    title: "10 in a row",
    description: "Get 10 math answers right in a row",
    earned: (profile) => (mathProgress(profile)?.maxCorrectStreak ?? 0) >= 10,
  },
  {
    id: "math-streak-20",
    family: "math-streak",
    subject: "math",
    number: 20,
    title: "20 in a row",
    description: "Get 20 math answers right in a row",
    earned: (profile) => (mathProgress(profile)?.maxCorrectStreak ?? 0) >= 20,
  },
  {
    id: "math-level-5",
    family: "math-level",
    subject: "math",
    number: 5,
    title: "Level 5",
    description: "Reach math level 5",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 5,
  },
  {
    id: "math-level-20",
    family: "math-level",
    subject: "math",
    number: 20,
    title: "Level 20",
    description: "Reach math level 20",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 20,
  },
  {
    id: "math-level-50",
    family: "math-level",
    subject: "math",
    number: 50,
    title: "Level 50",
    description: "Reach math level 50",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 50,
  },
  {
    id: "math-right-10",
    family: "math-right",
    subject: "math",
    number: 10,
    title: "10 right",
    description: "Get 10 math answers right",
    earned: (profile) => mathRightCount(profile) >= 10,
  },
  {
    id: "math-right-50",
    family: "math-right",
    subject: "math",
    number: 50,
    title: "50 right",
    description: "Get 50 math answers right",
    earned: (profile) => mathRightCount(profile) >= 50,
  },
  {
    id: "math-right-200",
    family: "math-right",
    subject: "math",
    number: 200,
    title: "200 right",
    description: "Get 200 math answers right",
    earned: (profile) => mathRightCount(profile) >= 200,
  },
  {
    id: "math-addition-complete",
    family: "math-completion",
    subject: "math",
    number: 1,
    title: "Addition complete",
    description: "Complete all addition levels",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 26,
  },
  {
    id: "math-subtraction-complete",
    family: "math-completion",
    subject: "math",
    number: 2,
    title: "Subtraction complete",
    description: "Complete all subtraction levels",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 21,
  },
  {
    id: "math-skip-complete",
    family: "math-completion",
    subject: "math",
    number: 3,
    title: "Skip counting complete",
    description: "Complete all skip-counting levels",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 52,
  },
  {
    id: "math-multiplication-complete",
    family: "math-completion",
    subject: "math",
    number: 4,
    title: "Multiplication complete",
    description: "Complete all multiplication levels",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 96,
  },
  {
    id: "math-division-complete",
    family: "math-completion",
    subject: "math",
    number: 5,
    title: "Division complete",
    description: "Complete all division levels",
    earned: (profile) => (mathProgress(profile)?.level ?? 1) >= 100,
  },
  {
    id: "spelling-streak-5",
    family: "spelling-streak",
    subject: "spelling",
    number: 5,
    title: "5 in a row",
    description: "Get 5 spelling answers right in a row",
    earned: (profile) => (spellingProgress(profile)?.maxCorrectStreak ?? 0) >= 5,
  },
  {
    id: "spelling-streak-10",
    family: "spelling-streak",
    subject: "spelling",
    number: 10,
    title: "10 in a row",
    description: "Get 10 spelling answers right in a row",
    earned: (profile) => (spellingProgress(profile)?.maxCorrectStreak ?? 0) >= 10,
  },
  {
    id: "spelling-streak-20",
    family: "spelling-streak",
    subject: "spelling",
    number: 20,
    title: "20 in a row",
    description: "Get 20 spelling answers right in a row",
    earned: (profile) => (spellingProgress(profile)?.maxCorrectStreak ?? 0) >= 20,
  },
  {
    id: "spelling-level-5",
    family: "spelling-level",
    subject: "spelling",
    number: 5,
    title: "Level 5",
    description: "Reach spelling level 5",
    earned: (profile) => (spellingProgress(profile)?.level ?? 1) >= 5,
  },
  {
    id: "spelling-level-20",
    family: "spelling-level",
    subject: "spelling",
    number: 20,
    title: "Level 20",
    description: "Reach spelling level 20",
    earned: (profile) => (spellingProgress(profile)?.level ?? 1) >= 20,
  },
  {
    id: "spelling-level-50",
    family: "spelling-level",
    subject: "spelling",
    number: 50,
    title: "Level 50",
    description: "Reach spelling level 50",
    earned: (profile) => (spellingProgress(profile)?.level ?? 1) >= 50,
  },
  {
    id: "spelling-right-10",
    family: "spelling-right",
    subject: "spelling",
    number: 10,
    title: "10 right",
    description: "Get 10 spelling answers right",
    earned: (profile) => spellingRightCount(profile) >= 10,
  },
  {
    id: "spelling-right-50",
    family: "spelling-right",
    subject: "spelling",
    number: 50,
    title: "50 right",
    description: "Get 50 spelling answers right",
    earned: (profile) => spellingRightCount(profile) >= 50,
  },
  {
    id: "spelling-right-200",
    family: "spelling-right",
    subject: "spelling",
    number: 200,
    title: "200 right",
    description: "Get 200 spelling answers right",
    earned: (profile) => spellingRightCount(profile) >= 200,
  },
];

export const achievementSubject = (item: Achievement): "reading" | "math" | "spelling" | "shared" => item.subject;

export const achievementById = (id: string): Achievement | undefined => {
  return ACHIEVEMENTS.find((item) => item.id === id);
};

export const isFamilyCapstone = (item: Achievement): boolean => {
  const family = ACHIEVEMENTS.filter((entry) => entry.family === item.family);
  return family[family.length - 1]?.id === item.id;
};

export const earnedAchievementIds = (profile: Profile): string[] => {
  return ACHIEVEMENTS.filter((item) => item.earned(profile)).map((item) => item.id);
};

export const syncAchievements = (profile: Profile): Profile => {
  const peakCoins = Math.max(profile.peakCoins, profile.coins);
  const next = peakCoins === profile.peakCoins ? profile : { ...profile, peakCoins };
  const unlocked = [...new Set([...next.achievements, ...earnedAchievementIds(next)])];
  if (unlocked.length === next.achievements.length && unlocked.every((id) => next.achievements.includes(id))) {
    return next;
  }
  return { ...next, achievements: unlocked };
};

export const newAchievementIds = (previous: string[], next: string[]): string[] => {
  const had = new Set(previous);
  return next.filter((id) => !had.has(id));
};
